/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import { EuiFlexGroup, EuiFlexItem, EuiButtonIcon, EuiText, EuiLoadingSpinner } from '@elastic/eui'

type Props = {
  pageCount: number
  activePage: number
  onPageClick: (pageIndex: number) => void
  hasNextPage: boolean
  cursorHistory?: string[]
  'aria-label'?: string
  isLoading?: boolean
}

export default function CustomPagination({
  pageCount,
  activePage,
  onPageClick,
  hasNextPage,
  'aria-label': ariaLabel,
  isLoading = false,
}: Props) {
  const canGoToPrevious = activePage > 0 && !isLoading
  const canGoToNext = hasNextPage && !isLoading

  if (pageCount <= 1) {
    return null
  }

  return (
    <EuiFlexGroup
      alignItems='center'
      gutterSize='s'
      responsive={false}
      aria-label={ariaLabel || 'Pagination'}
    >
      {/* Previous button */}
      <EuiFlexItem grow={false}>
        <EuiButtonIcon
          size='s'
          iconType='arrowLeft'
          disabled={!canGoToPrevious}
          onClick={() => canGoToPrevious && onPageClick(activePage - 1)}
          aria-label='Go to previous page'
          data-test-id='pagination-button-previous'
        />
      </EuiFlexItem>

      {/* Current page indicator */}
      <EuiFlexItem grow={false}>
        <EuiText size='s' color='default'>
          <strong>{activePage + 1}</strong> of {pageCount}
        </EuiText>
      </EuiFlexItem>

      {/* Loading spinner */}
      {isLoading && (
        <EuiFlexItem grow={false}>
          <EuiLoadingSpinner size='m' />
        </EuiFlexItem>
      )}

      {/* Next button */}
      <EuiFlexItem grow={false}>
        <EuiButtonIcon
          size='s'
          iconType='arrowRight'
          disabled={!canGoToNext}
          onClick={() => canGoToNext && onPageClick(activePage + 1)}
          aria-label='Go to next page'
          data-test-id='pagination-button-next'
        />
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}
